import {Banner, Button, CounterLabel, Stack} from '@primer/react'
import {Suspense, use, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentsSkeleton} from './CommentsSkeleton'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {LazyThreadsDataLoader, useShouldLoadLazyThreadData} from './LazyThreadsDataLoader'
import {ConversationItems} from './ConversationItems'
import type {ConversationItem} from './utils/build-conversation-items'
import {
  isIssueCommentComposerEnabled,
  IssueCommentComposer,
  type ComposerAdditionalActionsContext,
  type IssueCommentComposerHandle,
} from './IssueCommentComposer'
import {useLayoutCanComment, usePullRequestBasePath} from '../../stores/layout-selectors'
import {CommentsSort} from './use-comments-sort-state'
import {CommentsEmptyState, type CommentsEmptyStateVariant} from './CommentsEmptyState'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import type {CommentsDisplayMode} from './utils/display-mode'
import {CommentsContext} from './CommentsContext'
import {isDefaultFilterState, type CommentsFilterState} from './use-comments-filter-state'
import {ViewMode} from './utils/view-mode'

interface CommentsProps extends ConversationCommentsProps, Omit<ReviewCommentsProps, 'isLoadingThreads'> {
  displayMode: CommentsDisplayMode
}

interface ConversationCommentsProps extends CommonCommentsProps {
  hideCommentComposer?: boolean
  sort: CommentsSort
  /** Handle for interacting with the composer externally. */
  composerRef?: React.Ref<IssueCommentComposerHandle>
  /** Additional actions to render below the comment composer. */
  renderAdditionalComposerActions?: (context: ComposerAdditionalActionsContext) => React.ReactNode
  onSubmitted?: () => void
}

interface ReviewCommentsProps extends CommonCommentsProps {
  /** The maximum number of latest comments shown when the limit is exceeded. */
  limitCount?: number
  /** Whether the number of comments exceeds the displayable limit. */
  limitExceeded?: boolean
  isLoadingThreads?: boolean
}

interface CommonCommentsProps {
  handleQuoteReply: (quotedText: string) => void
  filteredConversationItems: ConversationItem[]
  /** Current filter state, used to tailor the empty state. */
  filterState: CommentsFilterState
  allReviewThreadsResolved?: boolean
  hasCurrentUserReviewThreads?: boolean
  /**
   * Called after the user activates a thread preview's link to its comment in the diff.
   * Navigating to the comment is handled internally, so this is only for side effects the
   * consumer wants to layer on top (e.g. closing a panel that renders these comments).
   */
  onNavigateToDiffComment?: () => void
  /** The login handle of the pull request author, used for issue comments rendering. */
  pullRequestAuthorLogin: string
  totalConversationItemsCount: number
}

/**
 * Renders pull request comments.
 *
 * Displays either thread previews or issue comments based on the current `viewMode`.
 * When in `thread-previews` mode and the comment limit is exceeded, a warning banner
 * is shown indicating that only the latest comments are visible.
 *
 * Content is lazily loaded with a skeleton fallback.
 */
export function Comments(props: CommentsProps) {
  const {shouldLazyLoadData, isLazyLoading: isLoadingThreads, onLazyDataLoaded} = useShouldLoadLazyThreadData()

  return (
    <>
      {shouldLazyLoadData && <LazyThreadsDataLoader onLoaded={onLazyDataLoaded} />}

      {props.displayMode === 'review-threads' ? (
        <ReviewComments {...props} isLoadingThreads={isLoadingThreads} />
      ) : (
        <ConversationComments {...props} />
      )}
    </>
  )
}

function ConversationComments({
  handleQuoteReply,
  filteredConversationItems,
  onNavigateToDiffComment,
  pullRequestAuthorLogin,
  totalConversationItemsCount: totalLoadedCount,
  hideCommentComposer,
  sort,
  composerRef,
  renderAdditionalComposerActions,
  onSubmitted,
}: ConversationCommentsProps) {
  const basePath = usePullRequestBasePath()
  const conversationCommentsQuery = useInfiniteConversationCommentsQuery({basePath})

  const totalCommentsCount = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')?.data?.conversationCount

  const showCommentComposer =
    isIssueCommentComposerEnabled('conversation', useLayoutCanComment()) && !hideCommentComposer

  const commentComposerAtTop = sort === CommentsSort.NewestFirst
  const commentComposerCollapsible = commentComposerAtTop

  const [commentComposerCollapsed, setCommentComposerCollapsed] = useState(true)

  const commentComposer = showCommentComposer && (
    <IssueCommentComposer
      ref={composerRef}
      collapsed={commentComposerCollapsible && commentComposerCollapsed}
      onCollapsedChange={commentComposerCollapsible ? setCommentComposerCollapsed : undefined}
      onSubmitted={onSubmitted}
      renderAdditionalActions={renderAdditionalComposerActions}
    />
  )

  const loadMoreButton = conversationCommentsQuery.hasNextPage && (
    <Stack align="center">
      <Button
        onClick={() => conversationCommentsQuery.fetchNextPage()}
        loading={conversationCommentsQuery.isFetchingNextPage}
        loadingAnnouncement="Loading previous comments"
        trailingVisual={
          totalCommentsCount ? (
            <CounterLabel style={{width: 'max-content'}}>{totalCommentsCount - totalLoadedCount}</CounterLabel>
          ) : undefined
        }
      >
        Load previous comments
      </Button>
    </Stack>
  )

  return (
    <Stack direction="vertical" gap="normal">
      {commentComposerAtTop && commentComposer}

      {totalLoadedCount > 0 && totalLoadedCount !== filteredConversationItems.length && (
        <p className="fgColor-muted text-small text-center m-0">
          Showing {filteredConversationItems.length} of {totalLoadedCount}
          {conversationCommentsQuery.hasNextPage ? <> loaded</> : <></>}
          <> comments</>
        </p>
      )}

      <ErrorBoundary boundaryName="Comments" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<CommentsSkeleton count={totalCommentsCount} />}>
          {conversationCommentsQuery.isError ? (
            <CommentsErrorFallback />
          ) : conversationCommentsQuery.isPending ? (
            // Temporary mess until we can remove the suspense and error boundary. We need to coalesce _all_ the different
            // loading and error states -- threads, pages of comments, initial comments....
            <CommentsSkeleton count={totalCommentsCount} />
          ) : totalLoadedCount === 0 ? (
            <CommentsEmptyState />
          ) : (
            <>
              {sort === CommentsSort.OldestFirst && loadMoreButton}
              <ConversationItems
                filteredConversationItems={filteredConversationItems}
                pullRequestAuthorLogin={pullRequestAuthorLogin}
                onNavigateToDiffComment={onNavigateToDiffComment}
                onQuoteReply={handleQuoteReply}
              />
              {sort === CommentsSort.NewestFirst && loadMoreButton}
            </>
          )}
        </Suspense>
      </ErrorBoundary>

      {!commentComposerAtTop && commentComposer}
    </Stack>
  )
}

interface ReviewCommentsProps {}

/** Returns a specialized empty state only when the default filters and loaded review-thread facts support it. */
function reviewCommentsEmptyStateVariant(
  viewMode: ViewMode,
  filterState: CommentsFilterState,
  totalItemsCount: number,
  allReviewThreadsResolved: boolean | undefined,
  hasCurrentUserReviewThreads: boolean | undefined,
  limitExceeded: boolean | undefined,
): CommentsEmptyStateVariant | undefined {
  if (!isDefaultFilterState(viewMode, filterState) || limitExceeded) return undefined
  if (viewMode === ViewMode.Mine) {
    if (hasCurrentUserReviewThreads === false) return 'mine'
    return totalItemsCount === 0 ? 'review-comments' : undefined
  }
  if (viewMode === ViewMode.ThreadPreviews) {
    if (allReviewThreadsResolved) return 'all-resolved'
    return totalItemsCount === 0 ? 'review-comments' : undefined
  }
  return 'review-comments'
}

function ReviewComments({
  handleQuoteReply,
  filteredConversationItems,
  limitCount,
  limitExceeded,
  onNavigateToDiffComment,
  pullRequestAuthorLogin,
  totalConversationItemsCount: totalItemsCount,
  allReviewThreadsResolved,
  hasCurrentUserReviewThreads,
  isLoadingThreads,
  filterState,
}: ReviewCommentsProps) {
  const {viewMode} = use(CommentsContext)
  const emptyStateVariant = reviewCommentsEmptyStateVariant(
    viewMode,
    filterState,
    totalItemsCount,
    allReviewThreadsResolved,
    hasCurrentUserReviewThreads,
    limitExceeded,
  )
  const hasFilteredItems = filteredConversationItems.length > 0
  const showEmptyState = !hasFilteredItems && (totalItemsCount === 0 || emptyStateVariant !== undefined)

  return (
    <Stack direction="vertical" gap="normal">
      {!showEmptyState && totalItemsCount > 0 && totalItemsCount !== filteredConversationItems.length && (
        <p className="fgColor-muted text-small text-center m-0">
          Showing {filteredConversationItems.length} of {totalItemsCount} review comments
        </p>
      )}

      {limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
        />
      )}

      <ErrorBoundary boundaryName="Comments" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<CommentsSkeleton reviewCommentsMode />}>
          {isLoadingThreads ? (
            // Temporary mess until we can remove the suspense and error boundary. We need to coalesce _all_ the different
            // loading and error states -- threads, pages of comments, initial comments....
            <CommentsSkeleton reviewCommentsMode />
          ) : showEmptyState ? (
            <CommentsEmptyState variant={emptyStateVariant ?? 'review-comments'} />
          ) : (
            <ConversationItems
              filteredConversationItems={filteredConversationItems}
              pullRequestAuthorLogin={pullRequestAuthorLogin}
              onNavigateToDiffComment={onNavigateToDiffComment}
              onQuoteReply={handleQuoteReply}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </Stack>
  )
}
